import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import BackToTop from '@/components/base/BackToTop';
import Footer from '@/pages/home/components/Footer';
import HydrogenHero from './components/HydrogenHero';
import HydrogenStats from './components/HydrogenStats';
import HydrogenBenefits from './components/HydrogenBenefits';
import HydrogenTypes from './components/HydrogenTypes';
import HydrogenApplications from './components/HydrogenApplications';
import HydrogenOpportunity from './components/HydrogenOpportunity';
import HydrogenFAQ from './components/HydrogenFAQ';
import HydrogenCTA from './components/HydrogenCTA';

const PAGE_TITLE = 'Hydrogen Energy Solutions Australia | Green Hydrogen Future — AK Energy';
const PAGE_DESCRIPTION = "Discover Australia's green hydrogen energy solutions. Learn about hydrogen production, applications, export opportunities, and connect with verified hydrogen providers and project developers on AK Energy.";

const pageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Hydrogen Energy Solutions Australia',
  description: PAGE_DESCRIPTION,
  url: 'https://ak-energyau.com/hydrogen-energy-australia',
  inLanguage: 'en-AU',
  isPartOf: {
    '@type': 'WebSite',
    name: 'AK Energy Australia',
    url: 'https://ak-energyau.com',
  },
  about: {
    '@type': 'Thing',
    name: 'Green Hydrogen',
    description: 'Hydrogen produced via water electrolysis powered by renewable energy, resulting in zero carbon dioxide emissions.',
  },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://ak-energyau.com/' },
      { '@type': 'ListItem', position: 2, name: 'Hydrogen Energy Australia', item: 'https://ak-energyau.com/hydrogen-energy-australia' },
    ],
  },
  relatedLink: [
    'https://ak-energyau.com/solar-energy-australia',
    'https://ak-energyau.com/wind-energy-solutions',
  ],
};

export default function HydrogenEnergyPage() {
  useEffect(() => {
    document.title = PAGE_TITLE;

    const metaDesc = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (metaDesc) metaDesc.content = PAGE_DESCRIPTION;

    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonical) canonical.href = 'https://ak-energyau.com/hydrogen-energy-australia';

    const ogTitle = document.querySelector<HTMLMetaElement>('meta[property="og:title"]');
    if (ogTitle) ogTitle.content = 'Hydrogen Energy Solutions Australia | AK Energy';

    const ogDesc = document.querySelector<HTMLMetaElement>('meta[property="og:description"]');
    if (ogDesc) ogDesc.content = PAGE_DESCRIPTION;

    const ogUrl = document.querySelector<HTMLMetaElement>('meta[property="og:url"]');
    if (ogUrl) ogUrl.content = 'https://ak-energyau.com/hydrogen-energy-australia';

    const twTitle = document.querySelector<HTMLMetaElement>('meta[name="twitter:title"]');
    if (twTitle) twTitle.content = 'Hydrogen Energy Solutions Australia | AK Energy';

    const twDesc = document.querySelector<HTMLMetaElement>('meta[name="twitter:description"]');
    if (twDesc) twDesc.content = PAGE_DESCRIPTION;

    const metaKw = document.querySelector<HTMLMetaElement>('meta[name="keywords"]');
    if (metaKw) {
      metaKw.content = 'hydrogen energy Australia, green hydrogen Australia, hydrogen production, green ammonia Australia, hydrogen export, National Hydrogen Strategy, hydrogen fuel cell';
    }

    return () => {
      document.title = 'AK Energy Australia | Renewable Energy Solutions Marketplace';
    };
  }, []);

  return (
    <main className="relative bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
      />
      <Navbar />
      <HydrogenHero />
      <HydrogenStats />
      <HydrogenBenefits />
      <HydrogenTypes />
      <HydrogenApplications />
      <HydrogenOpportunity />
      <HydrogenFAQ />
      <HydrogenCTA />
      <Footer />
      <BackToTop />
    </main>
  );
}